import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const FileWriteConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'content']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const encodings = [
    { value: 'utf-8', label: 'UTF-8', desc: 'Általános Unicode' },
    { value: 'utf-16', label: 'UTF-16', desc: 'Unicode 16-bit' },
    { value: 'ascii', label: 'ASCII', desc: 'Alapvető ASCII' },
    { value: 'latin1', label: 'Latin-1', desc: 'ISO-8859-1' },
  ];

  return (
    <div className="space-y-3">
      {/* Alapbeállítások */}
      <CollapsibleSection
        id="basic"
        title="Fájl Beállítások"
        icon="💾"
        color="rose"
        isExpanded={expandedSections.has('basic')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Fájl útvonal
            </label>
            <input
              type="text"
              value={node.data.config.file_path || ''}
              onChange={(e) => updateConfig('file_path', e.target.value)}
              className={commonStyles.input}
              placeholder="/path/to/output.txt vagy {{output_path}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Használj változókat vagy fix útvonalat
            </p>
          </div>

          <div>
            <label className={commonStyles.label}>
              Karakterkódolás
            </label>
            <div className="grid grid-cols-2 gap-2">
              {encodings.map((enc) => (
                <button
                  key={enc.value}
                  onClick={() => updateConfig('encoding', enc.value)}
                  className={`flex flex-col items-start p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.encoding || 'utf-8') === enc.value
                      ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium">{enc.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{enc.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="overwrite"
              checked={node.data.config.overwrite || false}
              onChange={(e) => updateConfig('overwrite', e.target.checked)}
              className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
            />
            <label htmlFor="overwrite" className="text-sm text-gray-700 dark:text-gray-300">
              Létező fájl felülírása
            </label>
          </div>

          {node.data.config.overwrite && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
              <div className="text-xs text-yellow-700 dark:text-yellow-300">
                <strong>⚠️ Figyelem:</strong> A létező fájl tartalma törlődik!
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Tartalom */}
      <CollapsibleSection
        id="content"
        title="Fájl Tartalom"
        icon="📝"
        color="blue"
        isExpanded={expandedSections.has('content')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Írandó tartalom
            </label>
            <textarea
              value={node.data.config.content || ''}
              onChange={(e) => updateConfig('content', e.target.value)}
              className={commonStyles.textarea}
              rows={6}
              placeholder="Fix szöveg vagy {{variable_name}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Használhatsz változókat a tartalomban
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <strong>💡 Változók:</strong> Használd a <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{"{{változó_név}}"}</code> formátumot az előző node-ok kimeneteihez.
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Elérhető Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A fájl írás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{success}}', desc: 'Sikeres írás', example: 'true' },
              { var: '{{file_path}}', desc: 'Fájl útvonal', example: '/path/to/file.txt' },
              { var: '{{bytes_written}}', desc: 'Írt bájtok', example: '1024' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Report generálás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              CRM adatok → Szöveg formázó → Fájl írás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📝 Log mentés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Flow eredmény → JSON → Fájl írás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>💾 Backup készítés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Adatbázis lekérdezés → JSON → Fájl írás
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default FileWriteConfig;
